import {useQueryClient} from '@tanstack/react-query';
import {Context, createContext, PropsWithChildren, useCallback, useContext, useMemo, useState} from 'react';

import {useUser} from '../../../../provider/user/user-hook';
import {fetchReviewItem, useReviews} from '../../../../service/reviews/reviews';
import {useReviewsCatalogList} from '../../../../service/reviews/reviews-catalogs';
import {useReviewsInfo} from '../../../../service/reviews/reviews-info';
import {useReviewsScrapyCatalogsInfo} from '../../../../service/reviews/reviews-scrapy-catalogs';
import {useReviewsSuggestions} from '../../../../service/reviews/reviews-suggestions';
import {FetchReviewsResultType, ReviewDataChildType, ReviewDataType} from '../../../../service/reviews/reviews-type';
import {MainFilterQueryKeyNameEnum} from '../../../../shared-search-parameters';
import {ProvidersIdsEnum} from '../../../../util/type';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {useNewReviewAiSuggest} from '../review-list-page-content/review-list/review/reply-form/new-neuralink-hook';
import {ReviewsFilterDataAsObjectUrlType} from '../review-list-page-content/reviews-filter/reviews-filter-type';
import {SuggestionMapType} from '../reviews-type';

import {getReviewStateFromUrlQuery} from './reviews-state-helper';
import {ReviewsStateContextType, ReviewsStateType, ReviewsStateUrlQueryNameEnum} from './reviews-state-type';

const ReviewsStateContext: Context<ReviewsStateContextType | null> = createContext<ReviewsStateContextType | null>(
    null
);

type PropsType = PropsWithChildren<Record<string, unknown>>;

// eslint-disable-next-line max-statements
export function ReviewsStateProvider(props: PropsType): JSX.Element {
    const {children} = props;
    const {user} = useUser();
    const [reviewsAnswer, setReviewsAnswer] = useState<Record<number, string>>({});
    const {setQuery: setReviewsStateUrlQuery, queries: reviewsQueries} = useUrl<
        | ReviewsFilterDataAsObjectUrlType
        | Record<MainFilterQueryKeyNameEnum, string>
        | Record<ReviewsStateUrlQueryNameEnum, string>
    >();
    const queryClient = useQueryClient();
    const reviewsState: ReviewsStateType = useMemo(() => getReviewStateFromUrlQuery(reviewsQueries), [reviewsQueries]);

    const setReviewAnswer = useCallback((reviewId: number, answer: string) => {
        setReviewsAnswer((previousState) => ({...previousState, [reviewId]: answer}));
    }, []);

    const appendReviewAnswerChunk = useCallback((reviewId: number, chunk: string) => {
        setReviewsAnswer((previous) => ({
            ...previous,
            [reviewId]: (previous[reviewId] || '') + chunk,
        }));
    }, []);

    const {requestSuggest, isGeneratingForReview, isBulkGenerate} = useNewReviewAiSuggest((textValue, reviewPk) =>
        appendReviewAnswerChunk(reviewPk, textValue)
    );

    const getReviewAnswer = useCallback(
        (reviewId: number) => {
            return reviewsAnswer[reviewId] || '';
        },
        [reviewsAnswer]
    );

    const {
        data: reviews = null,
        isFetching: isReviewsInProgress,
        refetch: refreshReviews,
        pagination: cursorPagination,
        queryKey,
        reviewIds,
    } = useReviews(reviewsState);

    const {
        data: reviewsCatalogList = null,
        isFetching: reviewsCatalogListIsInProgress,
        refetch: refreshCatalogs,
    } = useReviewsCatalogList();

    const googleMapsReviewIds = useMemo(
        () =>
            reviews?.results
                .filter((item) => item.catalogId === ProvidersIdsEnum.googleMapsNew)
                .map((review) => review.id) || [],
        [reviews]
    );

    const updateReview = useCallback(
        (reviewId: number, partialReviewData: Partial<ReviewDataType>) => {
            queryClient.setQueryData([queryKey], (oldData?: FetchReviewsResultType) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    results: oldData.results.map((review) => {
                        return reviewId === review.id ? {...review, ...partialReviewData} : review;
                    }),
                };
            });
        },
        [queryClient, queryKey]
    );

    const refreshReview = useCallback(
        async (reviewId: number) => {
            const response = await fetchReviewItem(reviewId);

            queryClient.setQueryData([queryKey], (oldData?: FetchReviewsResultType) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    results: oldData.results.map((review) => {
                        return response.id === review.id ? response : review;
                    }),
                };
            });
        },
        [queryClient, queryKey]
    );

    const refreshReviewWithAnswer = useCallback(
        (answer: ReviewDataChildType, reviewId: number) => {
            if (reviews && reviews.results) {
                queryClient.setQueryData([queryKey], (oldData?: FetchReviewsResultType) => {
                    if (!oldData) {
                        return oldData;
                    }

                    return {
                        ...oldData,
                        results:
                            oldData?.results.map((reviewItem) => {
                                if (reviewItem.id === reviewId) {
                                    return {
                                        ...reviewItem,
                                        processStatus: 'processed',
                                        children: [...(reviewItem?.children || []), answer],
                                    };
                                }

                                return reviewItem;
                            }) || [],
                    };
                });
            }
        },
        [queryClient, queryKey, reviews]
    );

    const {data: resultSuggestions, refetch: refreshSuggestions} = useReviewsSuggestions(reviewIds);
    const suggestionMap: SuggestionMapType = useMemo(() => {
        return Object.fromEntries(
            (resultSuggestions?.results || []).map((suggestion) => [suggestion.reviewId, suggestion])
        );
    }, [resultSuggestions?.results]);

    const {data: reviewsInfo = null, refetch: refreshReviewsInfo} = useReviewsInfo(reviewIds);
    const {data: reviewsScrapyCatalogsList} = useReviewsScrapyCatalogsInfo();

    const value: ReviewsStateContextType = useMemo(
        () => ({
            reviewsState,
            setReviewsStateUrlQuery,
            refreshReview,
            refreshReviews,
            reviewsCatalogList,
            reviewsCatalogListIsInProgress,
            reviewsScrapyCatalogsList,
            suggestionMap,
            refreshSuggestions,
            reviewsInfo,
            refreshReviewsInfo,
            refreshCatalogs,
            isReviewsInProgress,
            cursorPagination,
            reviews,
            reviewIds,
            googleMapsReviewIds,
            refreshReviewWithAnswer,
            updateReview,
            isShowAutotags: user?.profile?.isShowAutotags,
            reviewsAnswer,
            setReviewAnswer,
            getReviewAnswer,
            appendReviewAnswerChunk,
            isReplyGenerating: isGeneratingForReview,
            requestAIGenerate: requestSuggest,
            isBulkGenerate,
        }),
        [
            reviewsState,
            setReviewsStateUrlQuery,
            refreshReview,
            refreshReviews,
            reviewsCatalogList,
            reviewsCatalogListIsInProgress,
            reviewsScrapyCatalogsList,
            suggestionMap,
            refreshSuggestions,
            reviewsInfo,
            refreshReviewsInfo,
            refreshCatalogs,
            isReviewsInProgress,
            cursorPagination,
            reviews,
            reviewIds,
            googleMapsReviewIds,
            refreshReviewWithAnswer,
            updateReview,
            user?.profile?.isShowAutotags,
            reviewsAnswer,
            setReviewAnswer,
            getReviewAnswer,
            appendReviewAnswerChunk,
            isGeneratingForReview,
            requestSuggest,
            isBulkGenerate,
        ]
    );

    return <ReviewsStateContext.Provider value={value}>{children}</ReviewsStateContext.Provider>;
}

export function useReviewStateHook(): ReviewsStateContextType {
    const context: ReviewsStateContextType | null = useContext(ReviewsStateContext);

    if (!context) {
        throw new Error('useReviewStateHook must be used within a ReviewsStateProvider');
    }

    return context;
}
